import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BookOpen, Instagram, Music2, Youtube, Clapperboard, Sparkles,
  LayoutGrid, Mail, DollarSign, Star, ShieldCheck,
  Lock, Check, ArrowRight, Zap, TrendingUp, Users,
} from "lucide-react";
import { startCheckout } from "@/lib/checkout";
import BonusSection from "@/components/BonusSection";
import NewsletterSection from "@/components/NewsletterSection";

const LOGO = "/assets/logo.png";
const HERO_BG = "/assets/hero-bg.jpg";
const ACCENT_1 = "/assets/accent-1.jpg";
const ACCENT_2 = "/assets/accent-2.jpg";

const EBOOKS = [
  { icon: BookOpen, title: "The Ultimate Faceless Marketing Guide", tag: "Core Playbook", pages: 121, desc: "A step-by-step, in-depth blueprint to launching your very own faceless digital marketing business." },
  { icon: Youtube, title: "Faceless Youtube Automation For Passive Profits", tag: "Passive Income", pages: 128, desc: "Comprehensive guide to building faceless YouTube automation channels using AI." },
  { icon: Music2, title: "Faceless TikTok Guide for Digital Marketing", tag: "TikTok", pages: 99, desc: "A done-for-you guide to faceless marketing and going viral on TikTok." },
  { icon: Instagram, title: "The Ultimate Faceless Instagram Guide", tag: "Instagram", pages: 69, desc: "Navigate and grow a faceless Instagram account for digital marketing success." },
  { icon: Clapperboard, title: "Mastering Your Faceless Instagram Reels", tag: "Reels", pages: 47, desc: "Learn how to optimize your faceless account with high-impact, scroll-stopping reels." },
  { icon: Sparkles, title: "Chat GPT And Canva", tag: "AI & Design", pages: 76, desc: "A beginner's guide to crafting digital products using ChatGPT and Canva from A to Z." },
  { icon: TrendingUp, title: "Faceless Digital Marketing", tag: "Blueprint", pages: 43, desc: "Discover 19 proven sales strategies tailored for Instagram and email marketing." },
  { icon: LayoutGrid, title: "Mastering Etsy and Pinterest MRR", tag: "Master Resell Rights", pages: 63, desc: "A comprehensive guide to selling digital products on Etsy and Pinterest." },
  { icon: Users, title: "Mastering The World Of Social Media", tag: "Social Media", pages: 84, desc: "A beginner's guide to building and growing your own online presence." },
  { icon: Mail, title: "eMail Marketing For Beginners", tag: "Email", pages: 67, desc: "Go from novice to expert by mastering the fundamentals of email marketing for digital success." },
  { icon: DollarSign, title: "TikTok Money Guide", tag: "Monetize", pages: 60, desc: "This complete 2026 TikTok Money Guide breaks down 7 proven ways creators are earning today." },
];

const REVIEWS = [
  {
    name: "Mike",
    handle: "Faceless YouTube Creator",
    text: "I made my first $4k in 6 weeks without ever turning on a camera. The YouTube automation guide alone paid for the whole bundle a hundred times over.",
  },
  {
    name: "Charmaine",
    handle: "Digital Product Seller",
    text: "I was terrified of showing my face online. This blueprint let me build a themepage to 80k followers and sell digital products completely anonymously. Life-changing.",
  },
  {
    name: "Delmar",
    handle: "Reels & TikTok Creator",
    text: "The 300 viral hooks are unreal. I just plug them into my faceless reels and one video hit 2.3M views. Easiest money I've ever made online.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Landing() {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("canceled")) {
      toast.error("Checkout canceled — your bundle is still waiting for you.");
    }
  }, []);

  const handleBuy = async () => {
    try {
      setLoading(true);
      await startCheckout();
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong starting checkout. Please try again.");
      setLoading(false);
    }
  };

  const GoldButton = ({ children, testid, className = "" }) => (
    <button
      data-testid={testid}
      onClick={handleBuy}
      disabled={loading}
      className={`btn-gold inline-flex items-center justify-center gap-3 px-10 py-5 text-base sm:text-lg ${className}`}
    >
      {loading ? "Processing…" : children}
      {!loading && <ArrowRight className="w-5 h-5" />}
    </button>
  );

  return (
    <div className="relative bg-[#0A0A0A] text-white overflow-x-hidden">
      {/* ===== Header ===== */}
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-black/70 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="The Faceless Bundle logo — faceless digital marketing ebooks to monetize social media and earn passive income" className="w-9 h-9 object-contain" />
            <span className="font-serif-display text-lg sm:text-xl tracking-[0.25em] font-semibold">
              THE FACELESS BUNDLE<span className="text-[#D4AF37]">.</span>
            </span>
          </div>
          <button
            data-testid="header-cta-button"
            onClick={handleBuy}
            disabled={loading}
            className="hidden sm:inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#D4AF37] hover:text-black transition-colors"
          >
            Get Access — $27
          </button>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-28 pb-20 grain"
      >
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Faceless content creator workspace with laptop and coffee — start a faceless digital marketing side hustle to make money online" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-[#0A0A0A]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-4xl">
            <motion.div
              initial="hidden" animate="show" variants={fadeUp}
              className="inline-flex items-center gap-2 border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-4 py-2 mb-8"
            >
              <Users className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">
                2,000+ creators already inside
              </span>
            </motion.div>

            <motion.h1
              initial="hidden" animate="show" custom={1} variants={fadeUp}
              className="font-serif-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight"
            >
              Make <span className="gold-shimmer">$50,000/Month</span> Without Ever Showing Your Face
            </motion.h1>

            <motion.p
              initial="hidden" animate="show" custom={2} variants={fadeUp}
              className="mt-8 text-lg sm:text-xl text-zinc-300 leading-relaxed max-w-2xl"
            >
              Learn how to create viral faceless content that drives traffic, sales, and
              passive income — no filming, no editing, no stress. Over 2,000 creators are
              already using this blueprint.
            </motion.p>

            <motion.div
              initial="hidden" animate="show" custom={3} variants={fadeUp}
              className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6"
            >
              <GoldButton testid="hero-cta-button">
                Get Your Faceless Content System Now
              </GoldButton>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                100% Money-Back Guarantee
              </div>
            </motion.div>

            <motion.div
              initial="hidden" animate="show" custom={4} variants={fadeUp}
              className="mt-16 flex flex-wrap gap-x-10 gap-y-4 text-sm text-zinc-400"
            >
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" /> 11 premium ebooks</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" /> Instant digital delivery</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" /> No camera required</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== Stats strip ===== */}
      <section className="border-y border-zinc-900 bg-[#0C0C0C]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-900">
          {[
            { icon: Zap, stat: "11", label: "In-depth ebooks" },
            { icon: Users, stat: "2,000+", label: "Active creators" },
            { icon: TrendingUp, stat: "300+", label: "Viral hooks included" },
          ].map((s, i) => (
            <div key={i} className="py-10 flex items-center justify-center gap-4">
              <s.icon className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <div className="font-serif-display text-3xl font-bold">{s.stat}</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== What You Get ===== */}
      <section data-testid="ebooks-section" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="relative"
            >
              <div className="absolute -inset-6 bg-[#D4AF37]/10 blur-3xl rounded-full" />
              <img
                src="/assets/faceless-vault.jpg"
                alt="The Complete Faceless Vault by Social Media Combo — 11 premium ebooks plus 3 bonus guides, including The Ultimate Faceless Marketing Guide, Faceless YouTube Automation, Faceless TikTok Guide, Ultimate Instagram Guide, Mastering Reels, Chat GPT and Canva, Faceless Digital Marketing, Etsy & Pinterest MRR, Mastering the World of Social Media, Email Marketing, and TikTok Money Guide"
                data-testid="bundle-banner-image"
                className="relative z-10 w-full max-w-lg mx-auto drop-shadow-[0_20px_60px_rgba(0,0,0,0.8)]"
              />
            </motion.div>

            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              custom={1} variants={fadeUp}
            >
              <span className="text-sm uppercase tracking-[0.25em] text-[#D4AF37]">What you get</span>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 leading-tight">
                The Complete 11-Ebook Blueprint
              </h2>
              <p className="mt-6 text-lg text-zinc-400">
                Everything you need to launch, grow, and monetize a faceless content empire —
                across every major platform. Instantly downloadable, yours for life. Whether 
                you're a beginner looking to learn the ropes of faceless digital marketing or 
                an experienced marketer aiming to diversify your income streams, this bundle 
                offers the tools and knowledge you need to succeed. It's not just an educational 
                resource; it's a complete business opportunity. Rebrand the content to fit your 
                personal brand and enjoy 100% of the profits from your sales!! Or give them away 
                for free to your premiun subscribers. Everyone loves a Free ebook!
              </p>
              <div className="mt-8 flex items-center gap-4">
                <span className="font-serif-display text-2xl font-bold line-through text-zinc-600">$197</span>
                <span className="font-serif-display text-4xl font-bold gold-shimmer">$27</span>
                <span className="text-xs uppercase tracking-widest text-zinc-500 border border-zinc-800 px-3 py-1">
                  86% off
                </span>
              </div>
              <button
                data-testid="ebooks-header-cta-button"
                onClick={handleBuy}
                disabled={loading}
                className="btn-gold mt-8 inline-flex items-center gap-3 px-8 py-4 text-sm"
              >
                {loading ? "Processing…" : "Get All 11 Guides"}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {EBOOKS.map((book, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
                custom={i % 3} variants={fadeUp}
                data-testid={`ebook-card-${i}`}
                className="group relative bg-[#121212] border border-zinc-800 p-7 hover:border-[#D4AF37]/50 transition-colors duration-300"
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 flex items-center justify-center border border-zinc-800 bg-black group-hover:border-[#D4AF37]/40 transition-colors">
                    <book.icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <span className="font-serif-display text-2xl text-zinc-700 group-hover:text-[#D4AF37]/60 transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-6 text-lg font-semibold leading-snug">{book.title}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{book.desc}</p>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-xs uppercase tracking-widest text-[#D4AF37]/80">
                    {book.tag}
                  </span>
                  <span className="text-xs text-zinc-600">·</span>
                  <span className="text-xs text-zinc-500">{book.pages} pages</span>
                </div>
              </motion.div>
            ))}

            {/* Value CTA card fills the 12th grid slot */}
            <div className="relative overflow-hidden border border-[#D4AF37]/40 p-7 flex flex-col justify-between">
              <img src={ACCENT_1} alt="Moody laptop desk setup for building a faceless Instagram and TikTok passive income business from home" className="absolute inset-0 w-full h-full object-cover opacity-20" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
              <div className="relative z-10">
                <span className="text-xs uppercase tracking-widest text-[#D4AF37]">Total value</span>
                <div className="mt-3 flex items-baseline gap-3">
                  <span className="font-serif-display text-4xl font-bold line-through text-zinc-600">$197</span>
                  <span className="font-serif-display text-5xl font-bold gold-shimmer">$27</span>
                </div>
              </div>
              <button
                data-testid="ebooks-cta-button"
                onClick={handleBuy}
                disabled={loading}
                className="btn-gold relative z-10 mt-6 w-full py-4 text-sm"
              >
                Claim The Bundle
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Testimonials ===== */}
      <section data-testid="testimonials-section" className="relative py-24 md:py-32 bg-[#0C0C0C] border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-sm uppercase tracking-[0.25em] text-[#D4AF37]">Why people love it</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4">
              What Creators Are Saying
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {REVIEWS.map((r, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
                custom={i} variants={fadeUp}
                data-testid={`testimonial-card-${i}`}
                className="bg-[#121212] border border-zinc-800 p-8 flex flex-col"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-5 h-5 text-[#D4AF37]" fill="#D4AF37" />
                  ))}
                </div>
                <p className="text-zinc-300 leading-relaxed flex-1">“{r.text}”</p>
                <div className="mt-8 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-black border border-[#D4AF37]/40 font-serif-display text-xl text-[#D4AF37]">
                    {r.name[0]}
                  </div>
                  <div>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500">{r.handle}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Bonuses ===== */}
      <BonusSection />

      {/* ===== Trust & Payment Footer ===== */}
      <section data-testid="checkout-section" className="relative py-24 md:py-32 grain overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={ACCENT_2} alt="Aesthetic coffee and workspace background — secure Stripe checkout to buy faceless digital marketing ebooks with master resell rights" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-black/90 to-[#0A0A0A]" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
            Ready to start your faceless empire?
          </h2>

          <div className="mt-8 flex items-center justify-center gap-4">
            <span className="font-serif-display text-3xl font-bold line-through text-zinc-600">$197</span>
            <span className="font-serif-display text-6xl font-bold gold-shimmer">$27</span>
          </div>
          <p className="mt-3 text-sm uppercase tracking-widest text-zinc-500">One-time payment · Lifetime access</p>

          <div className="mt-12">
            <GoldButton testid="footer-cta-button" className="w-full sm:w-auto">
              Buy Now — Get Instant Access
            </GoldButton>
          </div>

          {/* Trust badges */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            <div className="flex items-start gap-4 border border-zinc-800 bg-[#121212]/80 p-6">
              <ShieldCheck className="w-8 h-8 text-[#D4AF37] shrink-0" />
              <div>
                <div className="font-semibold">100% Money-Back</div>
                <div className="text-sm text-zinc-500">30-day guarantee, no questions asked.</div>
              </div>
            </div>
            <div className="flex items-start gap-4 border border-zinc-800 bg-[#121212]/80 p-6">
              <Lock className="w-8 h-8 text-[#D4AF37] shrink-0" />
              <div>
                <div className="font-semibold">Secure Checkout</div>
                <div className="text-sm text-zinc-500">Encrypted payment powered by Stripe.</div>
              </div>
            </div>
            <div className="flex items-start gap-4 border border-zinc-800 bg-[#121212]/80 p-6">
              <Zap className="w-8 h-8 text-[#D4AF37] shrink-0" />
              <div>
                <div className="font-semibold">Instant Delivery</div>
                <div className="text-sm text-zinc-500">Download all 11 ebooks immediately.</div>
              </div>
            </div>
          </div>

          {/* Secure checkout — Stripe */}
          <div className="mt-12 flex items-center justify-center gap-2 text-sm text-zinc-500">
            <Lock className="w-4 h-4" />
            Checkout is secure and works instantly. Powered by
            <span className="font-extrabold tracking-tight text-[#635BFF]">stripe</span>
          </div>
        </div>
      </section>

      {/* ===== Newsletter / Free Ebook ===== */}
      <NewsletterSection />

      {/* ===== Bottom line ===== */}
      <footer className="border-t border-zinc-900 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-600">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="The Faceless Bundle logo — 11 faceless digital marketing ebooks to monetize social media and earn passive income" className="w-6 h-6 object-contain" />
            <span className="font-serif-display tracking-widest">THE FACELESS BUNDLE.</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-6">
              <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-[#D4AF37] transition-colors">Privacy</a>
              <a href="/terms" data-testid="footer-terms-link" className="hover:text-[#D4AF37] transition-colors">Terms</a>
            </div>
            <span>© {new Date().getFullYear()} socialmediacombo.com — All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
